<template>
  <div>
    <ProgressLoader
      v-if="loading"
      mode="indeterminate"
      color="#5AC396"
      size="150px"
    />
    <div v-else>
      <div v-if="stage !== 3">
        <div class="title">
          {{
            $t("briefcase.transferFrame.title") +
              ` ${mnemonicEng} ` +
              $t("briefcase.transferFrame.to")
          }}
        </div>
        <div class="remark">
          {{ $t("briefcase.transferFrame.pair") }}
        </div>
        <AuSelect
          class="mb-4"
          :value="receivedToken"
          :options="pairsOptions"
          @input="
            (v) => {
              receivedToken = v;
              showRefreshBtn = stage > 1;
              disableTransferBtn = true;
            }
          "
        />
        <div class="remark">
          {{ $t("briefcase.transferFrame.amount", { currency: mnemonicEng }) }}
        </div>
        <AuInput
          v-model="amount"
          class="mb-4"
          type="Number"
          @input="
            () => {
              showRefreshBtn = stage > 1;
              disableTransferBtn = true;
            }
          "
        />
      </div>
      <div
        v-if="stage === 2"
        class="mb-4"
      >
        <div>
          {{ $t("briefcase.transferFrame.available") }}:
          <span :class="{ 'exchange-info': !notEnough, 'error-string': notEnough }">
            {{ pairInfo.available + ` ${pairInfo.tokenTo}` }}
          </span>
        </div>
        <div>
          {{ $t("briefcase.transferFrame.price") }}:
          <span :class="{ 'exchange-info': !notEnough, 'error-string': notEnough }">
            {{ pairInfo.price + ` ${pairInfo.info.pair}` }}
          </span>
        </div>
        <div>
          {{ $t("briefcase.transferFrame.fee") }}:
          <span class="exchange-info">
            {{ fee }}
          </span>
        </div>
        <div>
          {{ $t("briefcase.transferFrame.amountReceived") }}:
          <span class="exchange-info">
            {{ pairInfo.value + ` ${pairInfo.tokenTo}` }}
          </span>
        </div>
        <div class="mt-4 mb-4">
          {{ warning }}
        </div>
      </div>
      <div
        v-if="stage === 3"
        class="mb-4"
      >
        <span v-if="order?.status === 'closed'">
          {{ $t("briefcase.transferFrame.success") }}
        </span>
        <span v-else>
          {{
            $t("briefcase.transferFrame.incomplete", {
              baseLeft: order?.amountBaseLeft,
              base: order?.amountBase
            })
          }}
          <a
            :href="systemSettings?.exchangeUrl"
            target="_blank"
            class="exchange-info"
          >
            {{ $t("briefcase.transferFrame.platform") }}
          </a>
        </span>
      </div>
      <div
        v-if="error.length > 0"
        class="error-string mb-4"
      >
        {{ error }}
      </div>
      <div class="flex gap-4">
        <AuButton
          class="mb-3"
          type="primary"
          :is-progress="loadingBtn"
          :disabled="disableTransferBtn && showRefreshBtn"
          @click="doAction"
        >
          {{ btnText }}
        </AuButton>
        <AuButton
          v-if="stage === 2 && showRefreshBtn"
          class="mb-3"
          type="primary"
          :is-progress="loadingBtn"
          @click="getPairExchangeInfo"
        >
          {{ $t("briefcase.transferFrame.refreshBtn") }}
        </AuButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import AuButton from "@/components/extended/AuButton.vue";
import AuInput from "@/components/extended/AuInput.vue";
import AuSelect from "@/components/extended/AuSelect.vue";
import AxiosController from "@/api/axiosController";
import BigNumber from "bignumber.js";
import i18n from "@/localization/localization.js";
import ProgressLoader from "@/loaders/progress-bar";
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toastification";

const props = defineProps({
  mnemonicEng: {
    type: String,
    default: ""
  },
  available: {
    type: String,
    default: "0"
  },
  decimals: {
    type: Number,
    default: 2
  }
});

const emit = defineEmits(["update"]);
const store = useStore();

const systemSettings = store.getters.apiSystemSettings;
const axiosController = new AxiosController();
const toast = useToast();

const stage = ref(1);
const loading = ref(true);
const loadingBtn = ref(false);
const pairsOptions = ref([]);
const receivedToken = ref("");
const amount = ref("");
const showRefreshBtn = ref(false);
const disableTransferBtn = ref(false);
const pairInfo = ref({});
const order = ref([]);
const warning = ref("");
const error = ref("");

const notEnough = computed(() => {
  return BigNumber(pairInfo.value.price).isGreaterThan(
    pairInfo.value.available
  );
});

const balance = computed(() => {
  return BigNumber(props.available).dividedBy(
    BigNumber(10).pow(props.decimals)
  );
});

const btnText = computed(() => {
  return stage.value === 1
    ? i18n.global.t("briefcase.transferFrame.continueBtn")
    : stage.value === 2
      ? i18n.global.t("briefcase.transferFrame.transferBtn")
      : i18n.global.t("briefcase.transferFrame.backBtn");
});

const fee = computed(() => {
  const feeValue = BigNumber(amount.value)
    .multipliedBy(pairInfo.value.fee)
    .dividedBy(100)
    .toString(10);
  return `${pairInfo.value.fee}% (${feeValue} ${props.mnemonicEng})`;
});

onMounted(async () => {
  try {
    pairsOptions.value = await axiosController.getTradingPairs({
      token: props.mnemonicEng
    });
    pairsOptions.value = pairsOptions.value.map((el) => ({
      display: el?.title,
      value: el?.id
    }));
  }
  catch (e) {
    error.value = e.response.data?.message || e.response.data?.detail;
  }
  loading.value = false;
});

const doAction = async () => {
  switch (stage.value) {
    case 1:
      await getPairExchangeInfo();
      break;
    case 2:
      await createOrderExchange();
      break;
    case 3:
      stage.value = 1;
      break;
  }
};

const check = () => {
  if (receivedToken.value.length === 0) {
    error.value = i18n.global.t("briefcase.transferFrame.errorToken");
    return false;
  }
  if (amount.value.length === 0) {
    error.value = i18n.global.t("briefcase.transferFrame.errorAmount");
    return false;
  }
  if (BigNumber(amount.value).isLessThanOrEqualTo(0)) {
    error.value = i18n.global.t("briefcase.transferFrame.errorZero");
    return false;
  }
  if (balance.value.isLessThan(amount.value)) {
    error.value = i18n.global.t("briefcase.transferFrame.errorNotEnough");
    return false;
  }
  return true;
};

const getPairExchangeInfo = async () => {
  if (!check()) {
    return;
  }
  const params = {
    tokenFrom: props.mnemonicEng.toLowerCase(),
    tokenTo: receivedToken.value.toLowerCase(),
    value: amount.value
  };
  loadingBtn.value = true;
  try {
    pairInfo.value = await axiosController.getPairInfo(params);
    stage.value = 2;
    error.value = "";
  }
  catch (e) {
    error.value = e.response.data?.message || e.response.data?.detail;
  }
  warning.value = i18n.global.t("briefcase.transferFrame.warning");
  disableTransferBtn.value = false;
  loadingBtn.value = false;
};

const createOrderExchange = async () => {
  loadingBtn.value = true;
  const value = BigNumber(amount.value)
    .multipliedBy(BigNumber(10).pow(props.decimals))
    .toString(10);
  const params = {
    tokenFrom: pairInfo.value.tokenFrom.toLowerCase(),
    tokenTo: pairInfo.value.tokenTo.toLowerCase(),
    value: value,
    price: pairInfo.value.price
  };
  warning.value = i18n.global.t("briefcase.transferFrame.inProcess");
  try {
    const resp = await axiosController.createOrderTradingPair(params);
    if (resp?.error) {
      error.value = resp.error;
    }
    else {
      error.value = "";
      const buf = await axiosController.getPairStatus({
        orderIds: [resp.orderId]
      });
      if (buf.length === 0) {
        error.value = i18n.global.t("briefcase.transferFrame.errorWrong");
      }
      else {
        order.value = buf[0];
        stage.value = 3;
        amount.value = "";
        toast.info(i18n.global.t("briefcase.transferFrame.transferAdd"));
        emit("update", value);
      }
    }
  }
  catch (e) {
    error.value = e.response.data?.message || e.response.data?.detail;
  }
  loadingBtn.value = false;
};
</script>

<style scoped lang="scss">
.title {
  font-size: 20px;
}
.error-string {
  color: red;
}
.remark {
  color: var(--color-50);
}
.exchange-info {
  color: #279063;
}
</style>